import { PORTFOLIO_COMPANIES, PORTFOLIO_ASSETS, LEADERSHIP } from "@/data/content";
import { FEATURED_ARTICLES } from "@/data/articles";

/**
 * Static site pages with searchable EN + VI titles/descriptions.
 * Dynamic content (companies, assets, leaders, articles) is appended below.
 */
const STATIC_PAGES = [
  {
    to: "/",
    type: "page",
    typeLabel: "Home",
    typeLabel_vi: "Trang chủ",
    title: "Home",
    title_vi: "Trang chủ",
    description: "Building Vietnam's next institutional platforms.",
    description_vi: "Xây dựng nền tảng vững chắc cho các mô hình dài hạn tại Việt Nam.",
    keywords: "home landing intro overview institutional platform",
  },
  {
    to: "/about/who-we-are",
    type: "page",
    typeLabel: "About",
    typeLabel_vi: "Giới thiệu",
    title: "Who We Are",
    title_vi: "Về chúng tôi",
    description: "A long-term investment group building enduring institutional platforms.",
    description_vi: "Tập đoàn đầu tư dài hạn kiến tạo các nền tảng bền vững.",
    keywords: "about who we are unik holdings company",
  },
  {
    to: "/about/portfolio-companies",
    type: "page",
    typeLabel: "About",
    typeLabel_vi: "Giới thiệu",
    title: "Portfolio Companies",
    title_vi: "Các công ty thành viên",
    description: "Companies within the Unik Holdings ecosystem.",
    description_vi: "Danh sách các công ty thành viên trực thuộc Unik Holdings.",
    keywords: "portfolio companies members subsidiaries hanco chitta unik green property",
  },
  {
    to: "/about/leadership",
    type: "page",
    typeLabel: "About",
    typeLabel_vi: "Giới thiệu",
    title: "Leadership",
    title_vi: "Ban Lãnh đạo",
    description: "The leadership team steering Unik Holdings.",
    description_vi: "Đội ngũ lãnh đạo dẫn dắt Unik Holdings.",
    keywords: "leadership team management executives board chairman ceo",
  },
  {
    to: "/investments/approach",
    type: "page",
    typeLabel: "Investment",
    typeLabel_vi: "Đầu tư",
    title: "Investment Approach",
    title_vi: "Triết lý đầu tư",
    description: "Our long-term investment philosophy and framework.",
    description_vi: "Triết lý đầu tư dài hạn và khung định hướng chiến lược.",
    keywords: "investment approach strategy philosophy framework igen structural growth",
  },
  {
    to: "/investments/portfolio-assets",
    type: "page",
    typeLabel: "Investment",
    typeLabel_vi: "Đầu tư",
    title: "Portfolio Assets",
    title_vi: "Dự án tiêu biểu",
    description: "Core real assets across residential, office, healthcare and wellness.",
    description_vi: "Danh mục tài sản cốt lõi từ nhà ở, văn phòng đến chăm sóc sức khỏe và nghỉ dưỡng.",
    keywords: "portfolio assets real estate healthcare wellness projects",
  },
  {
    to: "/impact",
    type: "page",
    typeLabel: "Impact",
    typeLabel_vi: "Kiến tạo giá trị",
    title: "Impact",
    title_vi: "Kiến tạo giá trị",
    description: "Research, education, and human capital investments.",
    description_vi: "Đầu tư vào nghiên cứu, giáo dục và năng lực con người.",
    keywords: "impact community education research knowledge platform",
  },
  {
    to: "/insights",
    type: "page",
    typeLabel: "Insights",
    typeLabel_vi: "Tin tức & Sự kiện",
    title: "Insights",
    title_vi: "Tin tức & Sự kiện",
    description: "Newsroom, research, and long-form perspectives.",
    description_vi: "Tin tức, nghiên cứu và các bài phân tích chuyên sâu.",
    keywords: "insights news press media announcements articles blog",
  },
  {
    to: "/about/careers",
    type: "page",
    typeLabel: "Careers",
    typeLabel_vi: "Cơ hội nghề nghiệp",
    title: "Careers",
    title_vi: "Cơ hội nghề nghiệp",
    description: "Join our team and build alongside us.",
    description_vi: "Gia nhập đội ngũ và cùng kiến tạo giá trị bền vững.",
    keywords: "careers jobs hiring team join talent recruitment",
  },
  {
    to: "/contact",
    type: "page",
    typeLabel: "Contact",
    typeLabel_vi: "Liên hệ",
    title: "Contact Us",
    title_vi: "Liên hệ",
    description: "Get in touch with the Unik Holdings team.",
    description_vi: "Kết nối với đội ngũ Unik Holdings.",
    keywords: "contact email phone address hanoi lieu giai",
  },
];

const norm = (s) => (s || "").toString().toLowerCase();

const flattenBody = (body) =>
  !Array.isArray(body)
    ? ""
    : body
        .map((b) => (typeof b === "string" ? b : b?.text || ""))
        .join(" ");

export function buildSearchIndex() {
  const items = [...STATIC_PAGES];

  // Portfolio companies (About → Portfolio Companies → Company Detail)
  PORTFOLIO_COMPANIES.forEach((c) => {
    items.push({
      to: `/portfolio/${c.slug}`,
      type: "company",
      typeLabel: "Portfolio Company",
      typeLabel_vi: "Công ty thành viên",
      title: c.name,
      title_vi: c.name_vi || c.name,
      description: c.focus || c.sector || "",
      description_vi: c.focus_vi || c.sector_vi || "",
      img: c.img,
      keywords: `${c.name} ${c.sector || ""} ${c.focus || ""} ${(c.keyAssets || []).join(" ")}`,
    });
  });

  // Portfolio assets
  PORTFOLIO_ASSETS.forEach((a) => {
    items.push({
      to: `/asset/${a.slug}`,
      type: "asset",
      typeLabel: "Portfolio Asset",
      typeLabel_vi: "Dự án tiêu biểu",
      title: a.name,
      title_vi: a.name_vi || a.name,
      description: `${a.type} — ${a.location}`,
      description_vi: `${a.type_vi || a.type} — ${a.location_vi || a.location}`,
      img: a.img,
      keywords: `${a.name} ${a.type} ${a.location} ${flattenBody(a.body)}`,
    });
  });

  // Leadership
  LEADERSHIP.forEach((l) => {
    items.push({
      to: `/leadership/${l.slug}`,
      type: "leader",
      typeLabel: "Leadership",
      typeLabel_vi: "Ban Lãnh đạo",
      title: l.name,
      title_vi: l.name_vi || l.name,
      description: l.role,
      description_vi: l.role_vi || l.role,
      img: l.img,
      keywords: `${l.name} ${l.role} ${l.short || ""}`,
    });
  });

  // Insights articles
  FEATURED_ARTICLES.forEach((a) => {
    items.push({
      to: `/insights/${a.slug}`,
      type: "article",
      typeLabel: a.category || "Insights",
      typeLabel_vi: a.category_vi || a.category || "Tin tức",
      title: a.title,
      title_vi: a.title_vi || a.title,
      description: a.excerpt,
      description_vi: a.excerpt_vi || a.excerpt,
      img: a.img,
      keywords: `${a.title} ${a.excerpt || ""} ${flattenBody(a.body)}`,
    });
  });

  return items;
}

/**
 * Simple case-insensitive substring match against title (both languages),
 * description (both languages), typeLabel, and keywords.
 */
export function searchIndex(query, lang = "en") {
  const q = norm(query);
  if (!q) return [];
  const index = buildSearchIndex();

  return index.filter((it) => {
    const haystack = [
      it.title,
      it.title_vi,
      it.description,
      it.description_vi,
      it.typeLabel,
      it.typeLabel_vi,
      it.keywords,
    ]
      .map(norm)
      .join(" | ");
    return haystack.includes(q);
  });
}
